"use strict";

class ProductsDataLayer {
  constructor() {
    this.dataLayer = window.dataLayer || [];
    this.selectors = {
      productGtm: ".gtm-card-product",
      cardProductWrapper: "product-card",
      productsGrid: "#product-grid",
      recentlyViewedProducts: "recently-viewed-products",
      recommendedProducts: "product-recommendations",
      cartRecommendedProducts: "[data-cart-recommended-products]",
    };
    this.cartItems = null;
    this.init();
  }

  init() {
    this.registerEvents();
    this.getProductsLists();
    this.getSingleProduct();
    this.getCartViewItems();
    this.magicZoomHandler();
  }

  registerEvents() {
    document.addEventListener("gtm:product-item-added", (e) => {
      this.setProductGtmData(
        ".product-information--inner",
        e.detail.element,
        e.detail.response,
        "add_to_cart"
      );
    });

    document.addEventListener("gtm:card-item-added", (e) => {
      this.setProductGtmData(
        ".product-card",
        e.detail.element,
        e.detail.response,
        "add_to_cart"
      );
    });

    document.addEventListener("gtm:cart-updated", (e) => {
      this.updateCartProducts(e.detail.element, e.detail.response);
    });

    document.addEventListener("gtm:initial-cart-updated", async (e) => {
      if (!e.detail?.element?.classList.contains("remove")) return;

      await fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((data) => {
          this.cartItems = data?.items;
        });
    });

    document.addEventListener("gtm:product-grid-updated", (e) => {
      this.getProductGridProducts(this.selectors.productsGrid, true);
    });

    document.addEventListener("gtm:pagination-used", (e) => {
      this.paginationDynamicProducts(e.detail);
    });

    document.addEventListener("gtm:trigger-swatch", (e) => {
      const { productId, isCard} = e?.detail
      if(!productId) return;
      if(isCard) return;

      this.dataLayer.push({
        event: 'change_color',
        product: productId
      })
    }, { passive: true });

    this.observeDynamicProducts();
  }

  resetEcommerce() {
    this.dataLayer.push({ecommerce: null});
  }

  getProductsLists() {
    const productLists = ["#product-grid"];

    productLists.forEach((selector) => {
      const productElements = document.querySelectorAll(selector);

      if (!productElements.length) return;

      this.processProductElements(productElements);
    });
  }

  paginationDynamicProducts(loadedItems) {
    const loadedItemsArray = Array.from(loadedItems);
    const loadedProductElements = loadedItemsArray.filter((product) =>
      product.querySelector(this.selectors.productGtm)
    );
    this.processProductElements(loadedProductElements);
  }

  observeDynamicProducts() {
    const productListsDynamic = [
      "product-recommendations",
      "recently-viewed-products",
      ".featured-collection",
      ".product-slider--container",
      ".product-recommendations--related",
    ];

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.getDynamicProducts([entry.target]);
            obs.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        threshold: 0.1,
      }
    );
    productListsDynamic.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el) => observer.observe(el));
    });

    this.observeCollectionTabs();
  }

  observeCollectionTabs() {
    const tabs = document.querySelectorAll("collection-tabs.collection-tabs__list");

    tabs.forEach((tabContainer) => {
      const targetSelector = `#${tabContainer.dataset.target}`;
      const productContainer = document.querySelector(targetSelector);

      if (!productContainer) return;

      const buttons = tabContainer.querySelectorAll("button.collection-tabs__list-link");

      const activeBtn = tabContainer.querySelector(
        "button.collection-tabs__list-link.active"
      );
      if (activeBtn) {
        const collectionName = activeBtn.dataset.collection;
        const productsToShow = Array.from(
          productContainer.querySelectorAll("[data-collection]")
        ).filter((product) => product.dataset.collection === collectionName);

        if (productsToShow.length) {
          this.getDynamicProducts(productsToShow);
        }
      }

      buttons.forEach((btn) => {
        btn.addEventListener("click", () => {
          const collectionName = btn.dataset.collection;

          const productsToShow = Array.from(
            productContainer.querySelectorAll("[data-collection]")
          ).filter((product) => product.dataset.collection === collectionName);

          if (productsToShow.length) {
            this.getDynamicProducts(productsToShow);
          }
        });
      });
    });
  }

  getProductGridProducts(selector, preventToPushEvent) {
    const productElements = document.querySelectorAll(selector);

    this.processProductElements(productElements, preventToPushEvent);
  }

  getDynamicProducts(element, preventToPushEvent) {
    this.processProductElements(element, preventToPushEvent);
  }

  getCurrentPageType() {
    const pagesList = {
      index: {
        item_list_id: "homepage",
        item_list_name: "Homepage",
      },
      collection: {
        item_list_id: "collection",
        item_list_name: "Collection page",
      },
      product: {
        item_list_id: "product",
        item_list_name: "Product page",
      },
      page: {
        item_list_id: "base_page",
        item_list_name: "Page",
      },
    };
    const currentPage = window.pageType || {
      item_list_id: "unknown",
      item_list_name: "Unknown",
    };

    return pagesList[currentPage];
  }

  processProductElements(productElements, preventToPushEvent) {
    const allItems = [];

    productElements.forEach((productList) => {
      const items = Array.from(
        productList.querySelectorAll(this.selectors.cardProductWrapper)
      )
        .map((product) => {
          const item = this.getGtmData(product);

          if (!item) return null;

          item.price = this.formatPrice(item.price);
          item.discount = this.formatPrice(item.discount);

          if (!item.item_list_id && !item.item_list_name) {
            Object.assign(item, this.getCurrentPageType());
          }

          return item;
        })
        .filter(Boolean);

      if (items.length === 0) return;

      allItems.push(...items);

      this.attachClickEvent();
    });

    if (preventToPushEvent) return;

    if (allItems.length) {
      this.addDataLayerForItemList(allItems);
    }
  }

  attachClickEvent() {
    const attachClickItems = document.querySelectorAll(
      `${this.selectors.cardProductWrapper}:not([data-gtm-click-bound])`
    );

    attachClickItems.forEach((product) => {
      if (product.dataset.gtmClickBound) return;

      const gtmDataProduct = product?.querySelector(this.selectors.productGtm);

      if (!gtmDataProduct) return;

      const gtmData = this.getGtmData(product);

      if (!gtmData) return;

      const productLinks = product.querySelectorAll("a");
      if (!productLinks.length) return;

      productLinks.forEach((link) => {
        link.addEventListener("click", () => {
          this.updateDataLayerForItemSelect(gtmData);
        });
      });

      product.dataset.gtmClickBound = "true";
    });
  }

  getGtmData(product) {
    const gtmDataContainer = product?.querySelector(this.selectors.productGtm);

    const data = JSON.parse(gtmDataContainer.textContent);

    return data;
  }

  getSingleProduct() {
    const product = document.querySelector(".product-information .product-form");

    if (!product) return;

    const gtmData = this.getGtmData(product);
    const price = this.formatPrice(gtmData?.price);
    const discount = this.formatPrice(gtmData?.discount);

    if (gtmData) {
      this.resetEcommerce();
      this.dataLayer.push({
        event: "view_item",
        ecommerce: {
          currency: gtmData.currency,
          value: price,
          items: [
            {
              ...gtmData,
              price,
              discount,
            },
          ],
        },
      });
    }
  }

  addDataLayerForItemList(items) {
    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_item_list",
      ecommerce: {
        item_list_id: items[0].item_list_id,
        item_list_name: items[0].item_list_name,
        items: items,
      },
    });
  }

  updateDataLayerForItemSelect(gtmData) {
    const price = this.formatPrice(gtmData?.price);
    const discount = this.formatPrice(gtmData?.discount);

    this.resetEcommerce();
    this.dataLayer.push({
      event: "select_item",
      ecommerce: {
        item_list_id: gtmData.item_list_id,
        item_list_name: gtmData.item_list_name,
        items: [
          {
            ...gtmData,
            price,
            discount,
          },
        ],
      },
    });
  }

  formatPrice(price) {
    return +(price / 100).toFixed(2);
  }

  getProductDiscount(response) {
    let discountInfo = {};

    if (response.line_level_discount_allocations?.length) {
      response.line_level_discount_allocations.filter((discounts) => {
        let discount = discounts.discount_application;
        let discountType = discount.value_type;
        let discountRate = discount.value;
        let discounted = "";
        let price = this.formatPrice(response.final_price);

        if (discountType === "percentage") {
          let discountDecimal = discountRate / 100;

          discounted = price / (1 - discountDecimal) - price;
        }

        if (discountType === "fixed_amount") {
          discounted = this.formatPrice(response.final_price) - discountRate;
        }

        discountInfo = {
          discount: +discounted ? +discounted.toFixed(2) : "",
          coupon: discount.title,
        };
      });
    } else if (response.discount) {
      discountInfo = {
        discount: this.formatPrice(response.discount),
      };
    }

    return discountInfo;
  }

  setProductGtmData(parentSelector, item, response, event, updateCartResponse = null) {
    if (item) {
      const product = item.closest(parentSelector);
      const gtmData = this.getGtmData(product) || {};

      let item_variant = gtmData.item_variant;
      let totalPrice = this.formatPrice(gtmData.price);
      let discounts = this.getProductDiscount(gtmData);
      let quantity = 1;
      let price = this.formatPrice(gtmData.price);

      if (response) {
        item_variant = response.options_with_values
          .map((opt) => `${opt.name}: ${opt.value}`)
          .join(", ");

        if (response.discounts.length) {
          discounts = this.getProductDiscount(response);
        }

        totalPrice = this.formatPrice(response.final_line_price);

        if (event === "add_to_cart" || event === "remove_from_cart") {
          const basePrice = this.formatPrice(response.price);
          const discount = response.line_level_discount_allocations?.length
            ? discounts.discount
            : 0;
          const finalUnitPrice = basePrice - discount;
          const itemsAdded = updateCartResponse?.items_added;
          const itemsRemoved = updateCartResponse?.items_removed;

          if (itemsAdded?.length > 0) {
            quantity = itemsAdded[0]?.quantity ?? 0;
          } else if (itemsRemoved?.length > 0) {
            quantity = itemsRemoved[0]?.quantity ?? 0;
          }

          totalPrice = finalUnitPrice * quantity;
        }
      }

      if (event !== "view_item_list" || event !== "select_item") {
        delete gtmData.item_list_name;
        delete gtmData.item_list_id;
        delete gtmData.index;
      }

      let data = {
        event: event,
        ecommerce: {
          currency: gtmData.currency,
          value: totalPrice,
          items: [
            {
              ...gtmData,
              ...discounts,
              price,
              quantity,
              item_variant,
            },
          ],
        },
      };

      this.cartItems = null;
      this.resetEcommerce();
      this.dataLayer.push(data);
    }
  }

  updateCartProducts(element, response) {
    if (!response || !element) return;

    let item = response.items_added?.[0] ?? response.items_removed?.[0];

    if (!item) return;

    const updatedItemId = item.variant_id;
    const updatedProduct =
      response.items.find((item) => item.variant_id === updatedItemId) ??
      this.cartItems.find((item) => item.variant_id === updatedItemId);

    if (!updatedProduct) return;

    let event = null;

    if (updatedProduct && response.items_added.length) {
      event = "add_to_cart";
    } else if (response.items_removed.length) {
      event = "remove_from_cart";
    }

    if (event) {
      this.setProductGtmData(
        ".product-cart-item",
        element,
        updatedProduct,
        event,
        response
      );
    }
  }

  getCartViewItems() {
    const cartPage = document.querySelector(".cart-items");

    if (!cartPage) return;

    let cartData = JSON.parse(
      document.querySelector("script#datalayer-cart-items")?.innerText
    );

    if (!cartData.length) return;

    cartData = cartData[0];

    cartData.items.map((item) => {
      item.price = this.formatPrice(item.price);

      if (item.line_level_discount_allocations?.length) {
        item.line_level_discount_allocations = item.line_level_discount_allocations.map(
          (discount) => {
            return {
              discount_application: {
                value_type: discount.value_type,
                value: discount.value,
                title: discount.title,
              },
            };
          }
        );
      }

      item.discount = this.getProductDiscount(item)?.discount;
      item.coupon = this.getProductDiscount(item)?.coupon;

      delete item.line_level_discount_allocations;
      delete item.final_price;
    });

    cartData.value = this.formatPrice(cartData.value);

    this.resetEcommerce();
    this.dataLayer.push({
      event: "view_cart",
      ecommerce: cartData,
    });
  }

  magicZoomHandler() {
    if (!window.MagicZoom || typeof MagicZoom.registerCallback !== 'function') {
      return;
    }

    let lastChangeBy = null;

    const registerCustomEvent = () => {
      const arrowEl = document.querySelectorAll('.MagicToolboxSlides .MagicToolboxSlides-arrow');
      const thumbsEl = document.querySelectorAll('.MagicToolboxSelectorsContainer .mz-thumb');

      arrowEl.forEach(el => {
        el.addEventListener('click', () => {
          lastChangeBy = null;

          lastChangeBy = {
            event: el.classList.contains('magic-prev') ? 'photo_previous' : 'photo_next'
          }
        })
      });

      thumbsEl.forEach(el => {
        el.addEventListener('click', () => {
          lastChangeBy = null;

          lastChangeBy = {
            event: 'photo_select'
          }
        })
      });
    };

    MagicZoom.registerCallback('onExpandOpen', zoomId => {
      const index = getActiveIndex(zoomId);
      const productId = getProductId();

      if(!index && !productId) return;

      this.dataLayer.push({
        event: 'photo_zoom',
        product: productId,
        index: index
      });
    });

    MagicZoom.registerCallback('onUpdate', (id, prev, newSelector) => {
      if(!lastChangeBy) return;

      const index = newSelector && +newSelector.closest('.mcs-item')?.dataset.item;
      const isLast = newSelector && !newSelector.closest('.mcs-item')?.nextSibling;

      if(isLast) {
        lastChangeBy = {
          event: 'photo_last'
        }
      }

      this.dataLayer.push({
        event: `${lastChangeBy.event}`,
        product: getProductId(),
        index: index
      })
    });

    registerCustomEvent();

    const getActiveIndex = (id) => {
      const el = document.querySelector(`.mz-thumb-selected[data-zoom-id="${id}"]`);
      return el && +el.closest('.mcs-item')?.dataset.item;
    };

    const getProductId = () => {
      return +document.querySelector('[name="product-id"]')?.value;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => new ProductsDataLayer());
